import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Button, CircularProgress } from '@mui/material';
import { AlertType, appendAlertItem } from '@pruforce/common-adminweb-sdk';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';
import AlertDialog from 'src/app/common/components/AlertDialog';
import { alertDialog } from 'src/utils/alertDialog';
import { cancelBdmBatchFile, fetchBdmBatchFile, submitBdmBatchFilePolicies } from '../../../network/opm-pd-bdm';
import { BatchFilesSubmitPageContext, BatchFilesSubmitPageContextType } from './BatchFilesSubmitPage.context';
import AdditionalInformation from './components/AdditionalInformation/AdditionalInformation';
import BatchFileActions from './components/BatchFileActions/BatchFileActions';
import ImportSummary from './components/ImportSummary/ImportSummary';
import PolicyTable from './components/PolicyTable/PolicyTable';

const BatchFilesSubmitPage = () => {
  const intl = useIntl();
  const Translation = (id: string, params: any = {}) => intl.formatMessage({ id }, params);
  const dispatch = useDispatch();
  const history = useHistory();
  const { bdmBatchFileId } = useParams<{ bdmBatchFileId: string }>();

  const [bdmBatchFile, setBdmBatchFile] = useState<BatchFilesSubmitPageContextType['bdmBatchFile']>(null);
  const [bdmBatchFileLoading, setBdmBatchFileLoading] =
    useState<BatchFilesSubmitPageContextType['bdmBatchFileLoading']>(false);
  const [formFiles, setFormFiles] = useState<any>(null);
  const [formPolicies, setFormPolicies] = useState<BatchFilesSubmitPageContextType['formPolicies']>([]);
  const [formNote, setFormNote] = useState('');
  const [deletedPolicyNumbers, setDeletedPolicyNumbers] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formPoliciesRef = useRef(formPolicies);

  const fetchBdmBatchFileData = async () => {
    setBdmBatchFileLoading(true);
    try {
      const data = await fetchBdmBatchFile(bdmBatchFileId, dispatch);
      setBdmBatchFile(data);
      setFormPolicies(
        data.policies.map((policy, index) => ({
          ...policy,
          index,
          qualifiedAgents: policy.qualifiedAgents?.filter((agent) => agent.agentCode !== policy.suggestedAgent) ?? [],
          selectedOtherQualifiedAgent: null,
          selectedOtherAgentReason: 'Priority for this Agent',
          displaySelectedAgent: policy.selectedAgent!,
          displaySelectedAgentName: policy.selectedAgentName!,
          isDeleted: false,
        })),
      );
    } catch (error) {
      history.push('/opm-pd/policy-transfer-request/batch-files');
    } finally {
      setBdmBatchFileLoading(false);
    }
  };

  const setPolicyFieldValue: BatchFilesSubmitPageContextType['setPolicyFieldValue'] = (index, field, value) => {
    const newPolicies = [...formPoliciesRef.current];
    newPolicies[index] = {
      ...newPolicies[index],
      [field]: value,
    };
    formPoliciesRef.current = newPolicies;
    setFormPolicies(newPolicies);
  };

  const addOtherQualifiedAgentToPolicy = (
    qualifiedAgents: {
      agentCode: string;
      fullname: string;
      label: string;
      policyNumbers: string[];
    }[],
  ) => {
    setFormPolicies((prevPolicies) => {
      const updatedPolicies = prevPolicies.map((policy) => {
        const matchedAgent = qualifiedAgents.find((agent) => agent.policyNumbers.includes(policy.policyNumber));
        if (matchedAgent && !policy.selectedOtherQualifiedAgent) {
          return {
            ...policy,
            selectedOtherQualifiedAgent: matchedAgent,
            displaySelectedAgent: matchedAgent.agentCode,
            displaySelectedAgentName: matchedAgent.fullname,
          };
        }
        return policy;
      });
      return updatedPolicies;
    });
  };

  const removeFormPolicy = (index: number | number[]) => {
    setFormPolicies((prevPolicies) => {
      const indices = Array.isArray(index) ? index : [index];
      setDeletedPolicyNumbers((prev) => [
        ...prev,
        ...prevPolicies.filter((policy) => indices.includes(policy.index)).map((policy) => policy.policyNumber),
      ]);
      return prevPolicies.map((policy) => (!indices.includes(policy.index) ? policy : { ...policy, isDeleted: true }));
    });
  };

  const _submitBdmBatchFilePolicies = async () => {
    let policies = formPolicies.filter((policy) => !policy.isDeleted);
    const invalidSelectedOtherAgentReasonPolicies = policies.filter((policy) => {
      return !!policy.selectedOtherQualifiedAgent && !policy.selectedOtherAgentReason;
    });
    if (invalidSelectedOtherAgentReasonPolicies.length > 0) {
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.ERROR,
            title: Translation('opm_pd.invalidInput'),
            content: Translation('opm_pd.provideReasonForSelectingAnotherAgentText', {
              policies: invalidSelectedOtherAgentReasonPolicies.map((policy) => policy.policyNumber).join(', '),
            }),
          },
        ]),
      );
      return;
    }
    const { isConfirmed } = await alertDialog.fire({
      title: Translation('opm_pd.confirmSubmitBatchFilePoliciesText'),
      description: Translation('opm_pd.confirmSubmitBatchFilePoliciesContentText'),
    });
    if (!isConfirmed) return;
    const notQualifiedAgentPolicyNumbers = policies
      .filter((policy) => {
        return !policy.displaySelectedAgent;
      })
      .map((policy) => policy.policyNumber);

    if (notQualifiedAgentPolicyNumbers.length > 0) {
      const { isConfirmed: isConfirmedSecond } = await alertDialog.fire({
        title: Translation('opm_pd.alert'),
        description: Translation('opm_pd.alertNumberOfPoliciesNotQualifiedAgentText', {
          count: notQualifiedAgentPolicyNumbers.length,
        }),
      });
      if (!isConfirmedSecond) return;
    }
    setIsSubmitting(true);
    try {
      await submitBdmBatchFilePolicies(
        {
          id: bdmBatchFileId,
          blobStorage: formFiles,
          polices: policies.map((policy) => ({
            policyNumber: policy.policyNumber,
            selectedAgent: policy.selectedOtherQualifiedAgent?.agentCode,
            selectedAgentName: policy.selectedOtherQualifiedAgent?.fullname,
            reasonSelectedAnotherAgent: !!policy.selectedOtherQualifiedAgent
              ? policy.selectedOtherAgentReason
              : undefined,
          })),
          notes: formNote,
          policesDeleted: [...deletedPolicyNumbers],
        },
        dispatch,
      );

      dispatch(
        appendAlertItem([
          {
            severity: AlertType.SUCCESS,
            title: Translation('opm_pd.success'),
            content: Translation('opm_pd.submitPoliciesSuccessfully'),
          },
        ]),
      );
      history.push(`/opm-pd/policy-transfer-request/batch-files/${bdmBatchFileId}/view`);
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  };

  const _cancelBdmBatchFile = async () => {
    const { isConfirmed } = await alertDialog.fire({
      title: Translation('opm_pd.confirmCancelBatchFilePoliciesText'),
      description: Translation('opm_pd.confirmCancelBatchFilePoliciesContentText'),
    });
    if (!isConfirmed) return;
    setIsSubmitting(true);
    try {
      await cancelBdmBatchFile(
        {
          id: bdmBatchFileId,
        },
        dispatch,
      );
      history.push(`/opm-pd/policy-transfer-request/batch-files/${bdmBatchFileId}/view`);
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    fetchBdmBatchFileData();
  }, [bdmBatchFileId]);

  useEffect(() => {
    formPoliciesRef.current = formPolicies;
  }, [formPolicies]);

  return (
    <BatchFilesSubmitPageContext.Provider
      value={{
        bdmBatchFile,
        bdmBatchFileLoading,
        formFiles,
        formPolicies,
        isSubmitting,
        formNote,
        cancelBdmBatchFile: _cancelBdmBatchFile,
        submitBdmBatchFilePolicies: _submitBdmBatchFilePolicies,
        setFormFiles,
        setFormNote,
        setPolicyFieldValue,
        addOtherQualifiedAgentToPolicy,
        removeFormPolicy,
      }}
    >
      <AlertDialog />
      <Box display="flex" flexDirection="column" gap={2}>
        <Box display="flex" justifyContent="flex-start" gap={2}>
          <Button
            startIcon={isSubmitting ? <CircularProgress size={20} /> : <ArrowBackIcon />}
            variant="outlined"
            color="error"
            disabled={isSubmitting}
            component={Link}
            to="/opm-pd/policy-transfer-request/batch-files"
          >
            Back
          </Button>
        </Box>
        {bdmBatchFileLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <ImportSummary />
            <PolicyTable />
            <AdditionalInformation />
            <BatchFileActions />
          </>
        )}
      </Box>
    </BatchFilesSubmitPageContext.Provider>
  );
};

export default BatchFilesSubmitPage;
